import { supabase } from "../../../auth/supabaseClient";
import {
  fetchOrganizationAccess,
  sendOrganizationInviteEmail,
} from "../../../core/settings/organizationAccessService";
import { getTileSettings, saveTileSetting } from "../../../core/settings/localSettingsService";

const CAMPUS_STUDENTS_TABLE = "campus_students";
const EDU_STUDENTS_TABLE = "edu_student_device_students";
const CAMPUS_STUDENT_PHOTO_BUCKET = "campus-student-photos";
const STUDENTS_TILE_ID = "students";
const PARENT_PORTAL_MODE = "parent_portal";
const CAMPUS_APP_ORIGIN = "https://oikoscampus.app";
const DEFAULT_STUDENT_ID_PREFIX = "STU-";
const DEFAULT_STUDENT_ID_NEXT_NUMBER = 1000;
const DEFAULT_STUDENT_ID_PAD_LENGTH = 4;
const DEFAULT_STUDENT_DEVICE_PIN = "0316";

function resolveCampusAppOrigin() {
  if (typeof window === "undefined") {
    return CAMPUS_APP_ORIGIN;
  }

  const hostname = String(window.location.hostname || "").toLowerCase();
  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".local")
  ) {
    return window.location.origin;
  }

  return CAMPUS_APP_ORIGIN;
}

function sanitizeFileName(name = "photo") {
  const cleaned = String(name || "photo")
    .toLowerCase()
    .replace(/[^a-z0-9.\-_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  return cleaned || "photo";
}

function createAvatarDataUrl(name = "Student") {
  const initials = String(name || "Student")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "S";

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="1024" height="1024" viewBox="0 0 1024 1024">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#0f766e" />
          <stop offset="100%" stop-color="#164e63" />
        </linearGradient>
      </defs>
      <rect width="1024" height="1024" rx="104" fill="url(#bg)" />
      <circle cx="512" cy="397" r="218" fill="rgba(255,255,255,0.18)" />
      <path d="M237 883c51-144 163-230 275-230s224 86 275 230" fill="rgba(255,255,255,0.18)" />
      <text x="50%" y="55%" dominant-baseline="middle" text-anchor="middle"
        font-family="Arial, sans-serif" font-size="275" font-weight="700" fill="#ffffff">${initials}</text>
    </svg>
  `;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function isMissingRelationError(error) {
  const message = String(error?.message || "").toLowerCase();

  return (
    error?.code === "42P01" ||
    error?.code === "42703" ||
    error?.code === "PGRST205" ||
    error?.code === "PGRST204" ||
    message.includes("does not exist") ||
    message.includes("could not find") ||
    message.includes("schema cache")
  );
}

function extractMissingColumnName(error) {
  const message = String(error?.message || "");
  const matched =
    message.match(/Could not find the '([^']+)' column/i) ||
    message.match(/column\s+["']?([^"'\s]+)["']?\s+does not exist/i);

  return matched?.[1] || "";
}

function stripMissingColumnFromPayload(payload, error) {
  const missingColumn = extractMissingColumnName(error);

  if (!missingColumn || !(missingColumn in payload)) {
    return null;
  }

  const nextPayload = { ...payload };
  delete nextPayload[missingColumn];
  return nextPayload;
}

function isUniqueViolation(error) {
  return (
    error?.code === "23505" ||
    (typeof error?.message === "string" &&
      error.message.toLowerCase().includes("duplicate key"))
  );
}

function isNoRowsReturnedError(error) {
  const message = String(error?.message || "").toLowerCase();
  return error?.code === "PGRST116" || message.includes("0 rows");
}

function normalizePositiveInteger(value, fallback) {
  const normalized = Number.parseInt(value, 10);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : fallback;
}

function buildStudentNumber(prefix, nextNumber, padLength) {
  return `${prefix}${String(nextNumber).padStart(padLength, "0")}`;
}

function normalizeStudent(row = {}) {
  const legalName = [row.legal_first_name, row.legal_middle_name, row.legal_last_name]
    .filter(Boolean)
    .join(" ");
  const preferredName = [row.preferred_first_name, row.preferred_last_name]
    .filter(Boolean)
    .join(" ");
  const displayName = row.display_name || preferredName || legalName || "Unnamed Student";

  return {
    id: row.id || crypto.randomUUID(),
    accountId: row.account_id || "",
    studentNumber: row.student_number || "",
    stateId: row.state_id || "",
    localId: row.local_id || "",
    campusPersonId: row.campus_person_id || "",
    legalFirstName: row.legal_first_name || "",
    legalMiddleName: row.legal_middle_name || "",
    legalLastName: row.legal_last_name || "",
    legalSuffix: row.legal_suffix || "",
    preferredFirstName: row.preferred_first_name || "",
    preferredLastName: row.preferred_last_name || "",
    displayName,
    dateOfBirth: row.date_of_birth || "",
    gender: row.gender || "",
    sex: row.sex || "",
    gradeLevel: row.grade_level || "",
    schoolName: row.school_name || "",
    campusName: row.campus_name || "",
    homeroomTeacher: row.homeroom_teacher || "",
    counselorName: row.counselor_name || "",
    enrollmentStatus: row.enrollment_status || "",
    currentEnrollmentStatus: row.current_enrollment_status || row.enrollment_status || "",
    enrollmentStartDate: row.enrollment_start_date || "",
    enrollmentEndDate: row.enrollment_end_date || "",
    graduationYear: row.graduation_year || "",
    tuitionPaymentStatus: row.tuition_payment_status || "",
    tuitionBalanceCents: Number(row.tuition_balance_cents || 0) || 0,
    primaryLanguage: row.primary_language || "",
    homeLanguage: row.home_language || "",
    ethnicity: row.ethnicity || "",
    raceEthnicity: Array.isArray(row.race_ethnicity) ? row.race_ethnicity : [],
    photoUrl: row.photo_url || "",
    photoPath: row.photo_path || "",
    householdName: row.household_name || "",
    primaryPhone: row.primary_phone || "",
    primaryEmail: row.primary_email || "",
    streetAddress1: row.street_address_1 || "",
    streetAddress2: row.street_address_2 || "",
    city: row.city || "",
    state: row.state || "",
    postalCode: row.postal_code || "",
    demographics: row.demographics || {},
    guardians: Array.isArray(row.guardians) ? row.guardians : [],
    emergencyContacts: Array.isArray(row.emergency_contacts) ? row.emergency_contacts : [],
    medical: row.medical || {},
    disciplinary: Array.isArray(row.disciplinary) ? row.disciplinary : [],
    parentContactLog: Array.isArray(row.parent_contact_log) ? row.parent_contact_log : [],
    enrollment: row.enrollment || {},
    programParticipation: row.program_participation || {},
    tags: Array.isArray(row.tags) ? row.tags : [],
    customFields: row.custom_fields || {},
    eduStudentDeviceStudentId:
      row.edu_student_device_student_id ||
      row.custom_fields?.eduStudentDeviceStudentId ||
      row.custom_fields?.eduStudentId ||
      "",
    deviceLoginName: row.device_login_name || row.custom_fields?.deviceLoginName || "",
    devicePin: row.device_pin || row.custom_fields?.devicePin || "",
    isActive: row.is_active !== false,
  };
}

function getStudentProfileImageUrl(student = {}) {
  const imageUrl = String(student.photoUrl || student.profileImageUrl || "").trim();

  if (!imageUrl || imageUrl.startsWith("data:image/svg+xml")) {
    return "";
  }

  return imageUrl;
}

function normalizeLoginName(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s.]+/g, "")
    .replace(/[^a-z0-9_-]+/g, "")
    .replace(/^[._-]+|[._-]+$/g, "")
    .slice(0, 64);
}

function normalizeGeneratedLoginName(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .slice(0, 64);
}

function deriveStudentLoginName(student = {}) {
  const requestedLogin = normalizeLoginName(student.deviceLoginName);
  const displayLogin = normalizeGeneratedLoginName(student.displayName);
  return (
    (requestedLogin && requestedLogin !== "newstudent" ? requestedLogin : "") ||
    (displayLogin && displayLogin !== "newstudent" ? displayLogin : "") ||
    normalizeGeneratedLoginName(
      [
        student.preferredFirstName || student.legalFirstName,
        student.preferredLastName || student.legalLastName,
      ]
        .filter(Boolean)
        .join(" ")
    ) ||
    normalizeGeneratedLoginName(student.studentNumber) ||
    normalizeLoginName(`student-${crypto.randomUUID().slice(0, 8)}`)
  );
}

function normalizeStudentPin(value = "") {
  const digits = String(value || "").replace(/\D+/g, "").slice(0, 4);
  return digits.length === 4 ? digits : "";
}

function isPlaceholderStudentLogin(loginName = "") {
  const login = normalizeLoginName(loginName);
  return /^stu[._-]?\d+/i.test(login) || /^student[._-]/i.test(login);
}

function chooseBestSharedStudent(student = {}, candidates = []) {
  const linkedId = student.eduStudentDeviceStudentId || "";

  return candidates
    .filter((candidate) => candidate?.id)
    .sort((a, b) => {
      const score = (candidate) => {
        const login = normalizeLoginName(candidate.login_name);
        let total = 0;

        if (candidate.campus_student_id && candidate.campus_student_id === student.id) total += 100;
        if (linkedId && candidate.id === linkedId) total += 80;
        if (candidate.is_active !== false) total += 20;
        if (isPlaceholderStudentLogin(login)) total -= 30;

        return total;
      };

      return score(b) - score(a);
    })[0] || null;
}

async function getUniqueStudentLoginName(accountId, preferredLoginName, currentStudentId = "") {
  const baseLoginName =
    normalizeLoginName(preferredLoginName) ||
    normalizeLoginName(`student-${crypto.randomUUID().slice(0, 8)}`);

  for (let index = 0; index < 50; index += 1) {
    const suffix = index === 0 ? "" : `.${index + 1}`;
    const candidate = `${baseLoginName.slice(0, 64 - suffix.length)}${suffix}`;
    let query = supabase
      .from(EDU_STUDENTS_TABLE)
      .select("id")
      .eq("account_id", accountId)
      .eq("login_name", candidate)
      .limit(1);

    if (currentStudentId) {
      query = query.neq("id", currentStudentId);
    }

    const { data, error } = await query;

    if (error) {
      if (isMissingRelationError(error)) {
        return candidate;
      }
      throw error;
    }

    if (!Array.isArray(data) || data.length === 0) {
      return candidate;
    }
  }

  return normalizeLoginName(`${baseLoginName}.${crypto.randomUUID().slice(0, 8)}`);
}

async function findExistingSharedStudent(accountId, student = {}) {
  const filters = [];

  if (student.eduStudentDeviceStudentId) {
    filters.push({ column: "id", value: student.eduStudentDeviceStudentId, requiresCampusStudentId: false });
  }

  if (student.id) {
    filters.push({ column: "campus_student_id", value: student.id, requiresCampusStudentId: true });
  }

  for (const filter of filters) {
    let result = await supabase
      .from(EDU_STUDENTS_TABLE)
      .select("id, campus_student_id, display_name, login_name, pin_code, grade_level, theme_color, is_active")
      .eq("account_id", accountId)
      .eq(filter.column, filter.value)
      .limit(1);

    if (result.error && isMissingRelationError(result.error) && !filter.requiresCampusStudentId) {
      result = await supabase
        .from(EDU_STUDENTS_TABLE)
        .select("id, display_name, login_name, pin_code, grade_level, theme_color, is_active")
        .eq("account_id", accountId)
        .eq(filter.column, filter.value)
        .limit(1);
    }

    const { data, error } = result;

    if (error) {
      if (isMissingRelationError(error)) {
        continue;
      }
      throw error;
    }

    if (Array.isArray(data) && data[0]?.id) {
      return data[0];
    }
  }

  return null;
}

async function saveCampusStudentSharedLink(accountId, student, sharedAccount) {
  if (!student?.id || !sharedAccount?.eduStudentDeviceStudentId) {
    return;
  }

  let payload = {
    custom_fields: {
      ...(student.customFields || {}),
      eduStudentDeviceStudentId: sharedAccount.eduStudentDeviceStudentId,
      deviceLoginName: sharedAccount.deviceLoginName,
      devicePin: sharedAccount.devicePin,
    },
    edu_student_device_student_id: sharedAccount.eduStudentDeviceStudentId,
  };

  while (true) {
    const { error } = await supabase
      .from(CAMPUS_STUDENTS_TABLE)
      .update(payload)
      .eq("id", student.id)
      .eq("account_id", accountId);

    if (!error) {
      return;
    }

    const strippedPayload = stripMissingColumnFromPayload(payload, error);
    if (strippedPayload) {
      payload = strippedPayload;
      continue;
    }

    throw error;
  }
}

async function updateSharedStudentProfileImage(studentId, profileImageUrl) {
  if (!studentId || !profileImageUrl) {
    return;
  }

  const { error } = await supabase
    .from(EDU_STUDENTS_TABLE)
    .update({ profile_image_url: profileImageUrl })
    .eq("id", studentId);

  if (error && !isMissingRelationError(error)) {
    throw error;
  }
}

async function ensureCampusStudentsHaveSharedAccounts(accountId, students = []) {
  if (!accountId || students.length === 0) {
    return students;
  }

  let sharedResult = await supabase
    .from(EDU_STUDENTS_TABLE)
    .select("id, campus_student_id, display_name, login_name, pin_code, grade_level, theme_color, profile_image_url, is_active")
    .eq("account_id", accountId);

  if (sharedResult.error && isMissingRelationError(sharedResult.error)) {
    sharedResult = await supabase
      .from(EDU_STUDENTS_TABLE)
      .select("id, display_name, login_name, pin_code, grade_level, theme_color, is_active")
      .eq("account_id", accountId);
  }

  const { data, error } = sharedResult;

  if (error) {
    if (isMissingRelationError(error)) {
      return students;
    }
    throw error;
  }

  const sharedStudents = Array.isArray(data) ? data : [];
  const sharedById = new Map(sharedStudents.map((student) => [student.id, student]));
  const sharedByCampusStudentId = new Map(
    sharedStudents
      .filter((student) => student.campus_student_id)
      .map((student) => [student.campus_student_id, student])
  );

  const ensuredStudents = [];

  for (const student of students) {
    const existingShared = chooseBestSharedStudent(student, [
      sharedByCampusStudentId.get(student.id),
      sharedById.get(student.eduStudentDeviceStudentId),
    ]);

    if (existingShared?.id) {
      const sharedAccount = {
        eduStudentDeviceStudentId: existingShared.id,
        deviceLoginName: existingShared.login_name || student.deviceLoginName || "",
        devicePin: existingShared.pin_code || student.devicePin || "",
      };
      await updateSharedStudentProfileImage(existingShared.id, getStudentProfileImageUrl(student));

      if (
        student.eduStudentDeviceStudentId !== sharedAccount.eduStudentDeviceStudentId ||
        student.deviceLoginName !== sharedAccount.deviceLoginName ||
        student.devicePin !== sharedAccount.devicePin
      ) {
        await saveCampusStudentSharedLink(accountId, student, sharedAccount);
      }

      ensuredStudents.push({
        ...student,
        ...sharedAccount,
        customFields: {
          ...(student.customFields || {}),
          eduStudentDeviceStudentId: sharedAccount.eduStudentDeviceStudentId,
          deviceLoginName: sharedAccount.deviceLoginName,
          devicePin: sharedAccount.devicePin,
        },
      });
      continue;
    }

    const sharedAccount = await syncEduStudentDeviceAccount(accountId, {
      ...student,
      eduStudentDeviceStudentId: "",
      deviceLoginName: await getUniqueStudentLoginName(accountId, deriveStudentLoginName(student)),
      devicePin: normalizeStudentPin(student.devicePin) || DEFAULT_STUDENT_DEVICE_PIN,
    });

    await saveCampusStudentSharedLink(accountId, student, sharedAccount);

    ensuredStudents.push({
      ...student,
      ...sharedAccount,
      customFields: {
        ...(student.customFields || {}),
        eduStudentDeviceStudentId: sharedAccount.eduStudentDeviceStudentId,
        deviceLoginName: sharedAccount.deviceLoginName,
        devicePin: sharedAccount.devicePin,
      },
    });
  }

  return ensuredStudents;
}

async function syncCampusTeacherStudentAssignments(accountId, students = []) {
  if (!accountId || students.length === 0) {
    return;
  }

  const sharedStudentIdByCampusStudentId = new Map(
    students
      .filter((student) => student.id && student.eduStudentDeviceStudentId)
      .map((student) => [student.id, student.eduStudentDeviceStudentId])
  );

  const teacherResult = await supabase
    .from("edu_teachers")
    .select("id, display_name, username, email")
    .eq("account_id", accountId);

  if (teacherResult.error && !isMissingRelationError(teacherResult.error)) {
    throw teacherResult.error;
  }

  const teacherByEmail = new Map(
    (teacherResult.data || [])
      .filter((teacher) => teacher.email)
      .map((teacher) => [String(teacher.email).toLowerCase(), teacher])
  );
  const teacherByUsername = new Map(
    (teacherResult.data || [])
      .filter((teacher) => teacher.username)
      .map((teacher) => [String(teacher.username).toLowerCase(), teacher])
  );
  const teacherByName = new Map();
  const duplicateTeacherNames = new Set();

  (teacherResult.data || []).forEach((teacher) => {
    const name = String(teacher.display_name || "").trim().toLowerCase();

    if (!name) {
      return;
    }

    if (teacherByName.has(name)) {
      duplicateTeacherNames.add(name);
      teacherByName.delete(name);
      return;
    }

    if (!duplicateTeacherNames.has(name)) {
      teacherByName.set(name, teacher);
    }
  });

  let staffResult = await supabase
    .from("campus_staff")
    .select("id, edu_teacher_id, display_name, email, metadata, student_assignments")
    .eq("account_id", accountId)
    .eq("is_active", true);

  if (staffResult.error && isMissingRelationError(staffResult.error)) {
    staffResult = await supabase
      .from("campus_staff")
      .select("id, display_name, email, metadata, student_assignments")
      .eq("account_id", accountId)
      .eq("is_active", true);
  }

  if (staffResult.error) {
    if (isMissingRelationError(staffResult.error)) {
      return;
    }
    throw staffResult.error;
  }

  for (const staff of staffResult.data || []) {
    const teacherId =
      staff.edu_teacher_id ||
      staff.metadata?.eduTeacherId ||
      teacherByEmail.get(String(staff.email || "").toLowerCase())?.id ||
      teacherByUsername.get(String(staff.metadata?.portalUsername || "").toLowerCase())?.id ||
      teacherByName.get(String(staff.display_name || "").trim().toLowerCase())?.id ||
      "";
    const campusStudentIds = Array.isArray(staff.student_assignments)
      ? staff.student_assignments.map((studentId) => String(studentId))
      : [];
    const sharedStudentIds = Array.from(
      new Set(campusStudentIds.map((studentId) => sharedStudentIdByCampusStudentId.get(studentId)).filter(Boolean))
    );

    if (!teacherId) {
      continue;
    }

    const { error: deleteError } = await supabase
      .from("edu_teacher_students")
      .delete()
      .eq("teacher_id", teacherId);

    if (deleteError) {
      if (isMissingRelationError(deleteError)) {
        return;
      }
      throw deleteError;
    }

    if (sharedStudentIds.length === 0) {
      continue;
    }

    const { error: insertError } = await supabase
      .from("edu_teacher_students")
      .insert(
        sharedStudentIds.map((studentId) => ({
          teacher_id: teacherId,
          student_id: studentId,
        }))
      );

    if (insertError && !isMissingRelationError(insertError)) {
      throw insertError;
    }
  }
}

async function signPhotoUrls(students = []) {
  const photoStudents = students.filter((student) => student.photoPath && !student.photoUrl);

  if (photoStudents.length === 0) {
    return students.map((student) => ({
      ...student,
      photoUrl: student.photoUrl || createAvatarDataUrl(student.displayName),
    }));
  }

  const uniquePaths = Array.from(new Set(photoStudents.map((student) => student.photoPath)));
  const { data, error } = await supabase.storage
    .from(CAMPUS_STUDENT_PHOTO_BUCKET)
    .createSignedUrls(uniquePaths, 60 * 60 * 24 * 30);

  if (error) {
    console.error("Campus student photo signing error:", error);
    return students.map((student) => ({
      ...student,
      photoUrl: student.photoUrl || createAvatarDataUrl(student.displayName),
    }));
  }

  const signedMap = new Map();
  (data || []).forEach((entry, index) => {
    if (entry?.signedUrl) {
      signedMap.set(uniquePaths[index], entry.signedUrl);
    }
  });

  return students.map((student) => ({
    ...student,
    photoUrl:
      student.photoUrl ||
      signedMap.get(student.photoPath) ||
      createAvatarDataUrl(student.displayName),
  }));
}

async function getCampusAccountId(userId) {
  const access = await fetchOrganizationAccess(userId, "campus");
  return access?.account?.id || null;
}

async function createStudentPhotoSignedUrl(path) {
  if (!path) {
    return "";
  }

  const { data, error } = await supabase.storage
    .from(CAMPUS_STUDENT_PHOTO_BUCKET)
    .createSignedUrl(path, 60 * 60 * 24 * 30);

  if (error) {
    console.error("Campus student photo signed URL error:", error);
    return "";
  }

  return data?.signedUrl || "";
}

export function getCampusStudentIdDefaults() {
  if (typeof window === "undefined") {
    return {
      prefix: DEFAULT_STUDENT_ID_PREFIX,
      nextNumber: DEFAULT_STUDENT_ID_NEXT_NUMBER,
      padLength: DEFAULT_STUDENT_ID_PAD_LENGTH,
    };
  }

  const settings = getTileSettings(STUDENTS_TILE_ID);
  const storedDefaults = settings?.studentIdDefaults || {};

  return {
    prefix:
      typeof storedDefaults.prefix === "string" && storedDefaults.prefix.trim()
        ? storedDefaults.prefix.trim()
        : DEFAULT_STUDENT_ID_PREFIX,
    nextNumber: normalizePositiveInteger(
      storedDefaults.nextNumber,
      DEFAULT_STUDENT_ID_NEXT_NUMBER
    ),
    padLength: normalizePositiveInteger(
      storedDefaults.padLength,
      DEFAULT_STUDENT_ID_PAD_LENGTH
    ),
  };
}

export function saveCampusStudentIdDefaults(defaults = {}) {
  const normalized = {
    prefix:
      typeof defaults.prefix === "string" && defaults.prefix.trim()
        ? defaults.prefix.trim()
        : DEFAULT_STUDENT_ID_PREFIX,
    nextNumber: normalizePositiveInteger(
      defaults.nextNumber,
      DEFAULT_STUDENT_ID_NEXT_NUMBER
    ),
    padLength: normalizePositiveInteger(
      defaults.padLength,
      DEFAULT_STUDENT_ID_PAD_LENGTH
    ),
  };

  saveTileSetting(STUDENTS_TILE_ID, "studentIdDefaults", normalized);
  return normalized;
}

export async function loadCampusStudents(userId) {
  if (!userId) {
    return [];
  }

  try {
    const accountId = await getCampusAccountId(userId);

    if (!accountId) {
      return [];
    }

    const { data, error } = await supabase
      .from(CAMPUS_STUDENTS_TABLE)
      .select("*")
      .eq("account_id", accountId)
      .eq("is_active", true)
      .order("grade_level", { ascending: true })
      .order("legal_last_name", { ascending: true })
      .order("legal_first_name", { ascending: true });

    if (error) {
      throw error;
    }

    const students = Array.isArray(data) ? data.map((row) => normalizeStudent(row)) : [];
    let hydratedStudents = students;

    try {
      hydratedStudents = await ensureCampusStudentsHaveSharedAccounts(accountId, students);
    } catch (syncError) {
      console.error("Campus student shared account sync error:", syncError);
    }

    try {
      await syncCampusTeacherStudentAssignments(accountId, hydratedStudents);
    } catch (assignmentError) {
      console.error("Campus teacher student assignment sync error:", assignmentError);
    }

    return signPhotoUrls(hydratedStudents);
  } catch (error) {
    console.error("Campus students load error:", error);
    return [];
  }
}

async function syncEduStudentDeviceAccount(accountId, student = {}) {
  const identityRequested =
    Boolean(student.id) ||
    Boolean(student.eduStudentDeviceStudentId) ||
    Boolean(String(student.deviceLoginName || "").trim()) ||
    Boolean(String(student.devicePin || "").trim());
  const existingSharedStudent = await findExistingSharedStudent(accountId, student);
  const sharedStudentId = existingSharedStudent?.id || student.eduStudentDeviceStudentId || "";
  const rawRequestedLoginName = normalizeLoginName(student.deviceLoginName);
  const requestedLoginName = rawRequestedLoginName === "newstudent" ? "" : rawRequestedLoginName;
  const existingLoginName = normalizeLoginName(existingSharedStudent?.login_name);
  const derivedLoginName = deriveStudentLoginName(student);
  const shouldReplacePlaceholderLogin =
    existingLoginName === "newstudent" && derivedLoginName && derivedLoginName !== "newstudent";
  const loginName =
    shouldReplacePlaceholderLogin
      ? await getUniqueStudentLoginName(accountId, derivedLoginName, sharedStudentId)
      : (
          requestedLoginName && requestedLoginName !== existingLoginName
            ? await getUniqueStudentLoginName(accountId, requestedLoginName, sharedStudentId)
            : existingSharedStudent?.login_name ||
              await getUniqueStudentLoginName(
                accountId,
                derivedLoginName,
                sharedStudentId
              )
        );
  const pin = normalizeStudentPin(student.devicePin);

  if (!identityRequested) {
    return {
      eduStudentDeviceStudentId: student.eduStudentDeviceStudentId || "",
      deviceLoginName: student.deviceLoginName || "",
      devicePin: student.devicePin || "",
    };
  }

  if (!pin) {
    throw new Error("Student device password/PIN must be exactly 4 digits.");
  }

  const payload = {
    account_id: accountId,
    campus_student_id: student.id || null,
    display_name: String(student.displayName || "").trim() || "Student",
    login_name: loginName,
    pin_code: pin,
    grade_level: String(student.gradeLevel || "").trim(),
    theme_color: student.themeColor || existingSharedStudent?.theme_color || "#d75c37",
    profile_image_url: getStudentProfileImageUrl(student),
    is_active: student.isActive !== false,
  };

  let data = null;
  let error = null;
  let nextPayload = payload;

  if (sharedStudentId) {
    const updateResult = await supabase
      .from(EDU_STUDENTS_TABLE)
      .update(nextPayload)
      .eq("id", sharedStudentId)
      .eq("account_id", accountId)
      .select("*")
      .single();

    data = updateResult.data;
    error = updateResult.error;

    const strippedPayload = stripMissingColumnFromPayload(nextPayload, error);
    if (strippedPayload) {
      nextPayload = strippedPayload;
      const retryUpdateResult = await supabase
        .from(EDU_STUDENTS_TABLE)
        .update(nextPayload)
        .eq("id", sharedStudentId)
        .eq("account_id", accountId)
        .select("*")
        .single();

      data = retryUpdateResult.data;
      error = retryUpdateResult.error;
    }

    if (isNoRowsReturnedError(error)) {
      data = null;
      error = null;
    }
  }

  if (!sharedStudentId || (!data && !error)) {
    const insertResult = await supabase
      .from(EDU_STUDENTS_TABLE)
      .insert(nextPayload)
      .select("*")
      .single();

    data = insertResult.data;
    error = insertResult.error;

    const strippedPayload = stripMissingColumnFromPayload(nextPayload, error);
    if (strippedPayload) {
      nextPayload = strippedPayload;
      const retryInsertResult = await supabase
        .from(EDU_STUDENTS_TABLE)
        .insert(nextPayload)
        .select("*")
        .single();

      data = retryInsertResult.data;
      error = retryInsertResult.error;
    }
  }

  if (error) {
    if (isUniqueViolation(error)) {
      const existingAfterConflict = await findExistingSharedStudent(accountId, student);

      if (existingAfterConflict?.id) {
        return {
          eduStudentDeviceStudentId: existingAfterConflict.id,
          deviceLoginName: existingAfterConflict.login_name || loginName,
          devicePin: existingAfterConflict.pin_code || pin,
        };
      }
    }

    throw error;
  }

  return {
    eduStudentDeviceStudentId: data?.id || "",
    deviceLoginName: data?.login_name || loginName,
    devicePin: data?.pin_code || pin,
  };
}

export function searchCampusStudents(students = [], query = "") {
  const normalized = String(query || "").trim().toLowerCase();

  if (!normalized) {
    return students;
  }

  return students.filter((student) =>
    [
      student.displayName,
      student.studentNumber,
      student.stateId,
      student.localId,
      student.gradeLevel,
      student.schoolName,
      student.homeroomTeacher,
      student.currentEnrollmentStatus,
      student.tuitionPaymentStatus,
      student.primaryEmail,
      student.primaryPhone,
      ...(student.tags || []),
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalized))
  );
}

export function getCampusStudentsWidgetStats(students = []) {
  const activeStudents = students.filter((student) => student.isActive);
  const gradeLabels = new Set(activeStudents.map((student) => student.gradeLevel).filter(Boolean));

  return {
    totalStudents: activeStudents.length,
    activeStudents: activeStudents.length,
    gradesRepresented: gradeLabels.size,
    latestStudent: activeStudents[0]?.displayName || "No students",
  };
}

export async function createCampusStudent(userId) {
  if (!userId) {
    throw new Error("Missing campus user.");
  }

  const accountId = await getCampusAccountId(userId);

  if (!accountId) {
    throw new Error("Missing campus organization.");
  }

  const defaults = getCampusStudentIdDefaults();
  let data = null;
  let createdStudentNumber = "";
  let nextNumberToPersist = defaults.nextNumber;

  for (let offset = 0; offset < 25; offset += 1) {
    const candidateNumber = defaults.nextNumber + offset;
    const uniqueSuffix = crypto.randomUUID().slice(0, 8).toUpperCase();
    const payload = {
      account_id: accountId,
      created_by: userId,
      display_name: "New Student",
      student_number: buildStudentNumber(
        defaults.prefix,
        candidateNumber,
        defaults.padLength
      ),
      state_id: `TEMP-${uniqueSuffix}`,
      enrollment_status: "Active",
      current_enrollment_status: "Currently Enrolled",
      tuition_balance_cents: 0,
      is_active: true,
    };

    let nextPayload = payload;

    while (true) {
      const result = await supabase
        .from(CAMPUS_STUDENTS_TABLE)
        .insert(nextPayload)
        .select("*")
        .single();

      if (!result.error) {
        data = result.data;
        createdStudentNumber = payload.student_number;
        nextNumberToPersist = candidateNumber + 1;
        break;
      }

      const strippedPayload = stripMissingColumnFromPayload(nextPayload, result.error);
      if (strippedPayload) {
        nextPayload = strippedPayload;
        continue;
      }

      if (isUniqueViolation(result.error)) {
        break;
      }

      throw result.error;
    }

    if (data) {
      break;
    }
  }

  if (!data) {
    throw new Error("Could not create a unique student ID from the current default settings.");
  }

  if (createdStudentNumber) {
    saveCampusStudentIdDefaults({
      ...defaults,
      nextNumber: nextNumberToPersist,
    });
  }

  const normalized = normalizeStudent(data);
  const sharedAccount = await syncEduStudentDeviceAccount(accountId, {
    ...normalized,
    deviceLoginName: deriveStudentLoginName(normalized),
    devicePin: DEFAULT_STUDENT_DEVICE_PIN,
  });

  const customFields = {
    ...(normalized.customFields || {}),
    eduStudentDeviceStudentId: sharedAccount.eduStudentDeviceStudentId,
    deviceLoginName: sharedAccount.deviceLoginName,
    devicePin: sharedAccount.devicePin,
  };

  let linkPayload = {
    custom_fields: customFields,
    edu_student_device_student_id: sharedAccount.eduStudentDeviceStudentId,
  };

  while (true) {
    const linkResult = await supabase
      .from(CAMPUS_STUDENTS_TABLE)
      .update(linkPayload)
      .eq("id", normalized.id)
      .eq("account_id", accountId);

    if (!linkResult.error) {
      break;
    }

    const strippedPayload = stripMissingColumnFromPayload(linkPayload, linkResult.error);
    if (strippedPayload) {
      linkPayload = strippedPayload;
      continue;
    }

    throw linkResult.error;
  }

  const [signed] = await signPhotoUrls([
    {
      ...normalized,
      customFields,
      ...sharedAccount,
    },
  ]);
  return signed;
}

function buildStudentPayload(student = {}) {
  return {
    legal_first_name: student.legalFirstName || "",
    legal_middle_name: student.legalMiddleName || "",
    legal_last_name: student.legalLastName || "",
    legal_suffix: student.legalSuffix || "",
    preferred_first_name: student.preferredFirstName || "",
    preferred_last_name: student.preferredLastName || "",
    display_name: student.displayName || "",
    student_number: student.studentNumber || "",
    state_id: student.stateId || "",
    local_id: student.localId || "",
    campus_person_id: student.campusPersonId || "",
    date_of_birth: student.dateOfBirth || null,
    gender: student.gender || "",
    sex: student.sex || "",
    grade_level: student.gradeLevel || "",
    school_name: student.schoolName || "",
    campus_name: student.campusName || "",
    homeroom_teacher: student.homeroomTeacher || "",
    counselor_name: student.counselorName || "",
    enrollment_status: student.enrollmentStatus || "",
    current_enrollment_status: student.currentEnrollmentStatus || "",
    enrollment_start_date: student.enrollmentStartDate || null,
    enrollment_end_date: student.enrollmentEndDate || null,
    graduation_year: student.graduationYear ? Number(student.graduationYear) : null,
    tuition_payment_status: student.tuitionPaymentStatus || "",
    tuition_balance_cents: Number(student.tuitionBalanceCents || 0) || 0,
    primary_language: student.primaryLanguage || "",
    home_language: student.homeLanguage || "",
    ethnicity: student.ethnicity || "",
    race_ethnicity: Array.isArray(student.raceEthnicity) ? student.raceEthnicity : [],
    photo_path: student.photoPath || "",
    photo_url:
      student.photoPath || student.photoUrl?.startsWith("data:image/svg+xml")
        ? ""
        : student.photoUrl || "",
    household_name: student.householdName || "",
    primary_phone: student.primaryPhone || "",
    primary_email: student.primaryEmail || "",
    street_address_1: student.streetAddress1 || "",
    street_address_2: student.streetAddress2 || "",
    city: student.city || "",
    state: student.state || "",
    postal_code: student.postalCode || "",
    demographics: student.demographics || {},
    guardians: Array.isArray(student.guardians) ? student.guardians : [],
    emergency_contacts: Array.isArray(student.emergencyContacts)
      ? student.emergencyContacts
      : [],
    medical: student.medical || {},
    disciplinary: Array.isArray(student.disciplinary) ? student.disciplinary : [],
    parent_contact_log: Array.isArray(student.parentContactLog) ? student.parentContactLog : [],
    enrollment: student.enrollment || {},
    program_participation: student.programParticipation || {},
    tags: Array.isArray(student.tags) ? student.tags : [],
    custom_fields: student.customFields || {},
    edu_student_device_student_id: student.eduStudentDeviceStudentId || null,
    is_active: student.isActive !== false,
  };
}

export async function updateCampusStudent(userId, student = {}) {
  let payload = buildStudentPayload(student);

  try {
    const accountId = await getCampusAccountId(userId);

    if (!accountId || !student?.id) {
      throw new Error("Missing campus account or student.");
    }

    const sharedAccount = await syncEduStudentDeviceAccount(accountId, student);
    payload = {
      ...payload,
      custom_fields: {
        ...(payload.custom_fields || {}),
        eduStudentDeviceStudentId: sharedAccount.eduStudentDeviceStudentId,
        deviceLoginName: sharedAccount.deviceLoginName,
        devicePin: sharedAccount.devicePin,
      },
      edu_student_device_student_id: sharedAccount.eduStudentDeviceStudentId || null,
    };

    while (true) {
      const { data, error } = await supabase
        .from(CAMPUS_STUDENTS_TABLE)
        .update(payload)
        .eq("id", student.id)
        .eq("account_id", accountId)
        .select("*")
        .single();

      if (!error) {
        const [signed] = await signPhotoUrls([
          {
            ...normalizeStudent(data),
            ...sharedAccount,
          },
        ]);
        return signed;
      }

      const strippedPayload = stripMissingColumnFromPayload(payload, error);
      if (strippedPayload) {
        payload = strippedPayload;
        continue;
      }

      if (!isMissingRelationError(error)) {
        throw error;
      }

      throw error;
    }
  } catch (error) {
    console.error("Campus student update error:", error);
    throw error;
  }
}

export async function inviteCampusParentPortal({
  userId,
  student,
  guardianIndex,
}) {
  if (!userId) {
    throw new Error("Missing campus user.");
  }

  if (!student?.id) {
    throw new Error("Missing student record.");
  }

  const guardians = Array.isArray(student.guardians) ? [...student.guardians] : [];
  const guardian = guardians[guardianIndex] || null;

  if (!guardian) {
    throw new Error("The selected guardian could not be found.");
  }

  if (!String(guardian.email || "").trim()) {
    throw new Error("Add the guardian email before sending parent portal access.");
  }

  const accountId = await getCampusAccountId(userId);

  if (!accountId) {
    throw new Error("Missing campus organization.");
  }

  const result = await sendOrganizationInviteEmail({
    accountId,
    email: String(guardian.email || "").trim(),
    recipientName: String(guardian.name || "").trim(),
    redirectTo: `${resolveCampusAppOrigin()}/parent/login`,
  });

  if (result?.invitedUserId) {
    guardians[guardianIndex] = {
      ...guardian,
      linkedUserId: result.invitedUserId,
    };
  }

  const nextStudent =
    guardians[guardianIndex]?.linkedUserId && guardians[guardianIndex]?.linkedUserId !== guardian.linkedUserId
      ? await updateCampusStudent(userId, {
          ...student,
          guardians,
        })
      : student;

  return {
    result,
    student: nextStudent,
    guardian: guardians[guardianIndex],
  };
}

export async function setCampusParentPortalAccess({
  userId,
  accountId,
  targetUserId,
  enabled,
}) {
  if (!userId || !accountId || !targetUserId) {
    throw new Error("Missing campus owner, organization, or parent account.");
  }

  const { data, error } = await supabase.rpc("campus_set_parent_portal_access", {
    account_uuid: accountId,
    target_user_id: targetUserId,
    enabled,
  });

  if (error) {
    throw error;
  }

  return data || null;
}

export async function uploadCampusStudentPhoto(userId, studentId, file) {
  if (!userId || !studentId) {
    throw new Error("Missing campus user or student record.");
  }

  if (!(file instanceof File)) {
    throw new Error("Please choose an image file to upload.");
  }

  const accountId = await getCampusAccountId(userId);

  if (!accountId) {
    throw new Error("Missing campus organization.");
  }

  const fileName = sanitizeFileName(file.name);
  const extension = fileName.includes(".") ? fileName.split(".").pop() : "jpg";
  const storagePath = `${accountId}/${studentId}/${Date.now()}-${crypto.randomUUID()}.${extension}`;

  const { error: uploadError } = await supabase.storage
    .from(CAMPUS_STUDENT_PHOTO_BUCKET)
    .upload(storagePath, file, {
      cacheControl: "3600",
      upsert: false,
    });

  if (uploadError) {
    throw uploadError;
  }

  const { data, error } = await supabase
    .from(CAMPUS_STUDENTS_TABLE)
    .update({
      photo_path: storagePath,
      photo_url: "",
    })
    .eq("id", studentId)
    .select("*")
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!data?.id) {
    throw new Error("Could not update this student photo. Please refresh Campus and try again.");
  }

  const normalized = normalizeStudent(data);
  const signedUrl = await createStudentPhotoSignedUrl(normalized.photoPath);
  await updateSharedStudentProfileImage(
    normalized.eduStudentDeviceStudentId,
    signedUrl
  );

  return {
    ...normalized,
    photoUrl: signedUrl || createAvatarDataUrl(normalized.displayName),
  };
}
